import type { ThemeTokens, ThemeVariant } from '../../contract.js';
import { defaultLight } from '../default/variants.js';

/**
 * Electric Light family — LIGHT-ONLY (single-mode, commit 6).
 *
 * Electric Light is a WHITE theme. Its single mode renders the higher-
 * contrast "dark accent on white" design: a white #FAFAFA surface with a
 * DARK accent (#2A2A2F) and dark-grey glow. The near-white #FAFAFA-accent
 * "airier" design that used to occupy the light slot was DROPPED — it read
 * as white-on-white on the CTA (the contrast bug Warren flagged).
 *
 * One token body, `electricLightTokens`, backs both variants. It is the
 * design Warren wants, and it sits in BOTH slots on purpose:
 *   - `variants.light` is the reachable one — `availableModes: ['light']`
 *     clamps the provider to `variants['light']`.
 *   - `variants.dark` reuses the same tokens only to satisfy the contract's
 *     both-variants requirement; the clamp makes it unreachable, and
 *     sharing the body means a future removal of availableModes can never
 *     surface a half-built dark variant.
 *
 * Spread base is `defaultLight.tokens` (not defaultDark): the surface is
 * white, so inherited semantic-status colours + chrome recipes should be
 * the light-surface versions.
 */
const electricLightTokens: ThemeTokens = {
  ...defaultLight.tokens,
  colors: {
    ...defaultLight.tokens.colors,
    // White-bg surface palette — surface slightly darker than bg, surface2
    // a tick further down, the same delta-spacing as Default light's
    // bg/surface/surface2 stack so cards sit visibly on top of the page.
    bg: '#FAFAFA',
    surface: '#F2F2F4',
    surface2: '#E8E8EB',
    // Light sidebar for this family. Diverges from the shared-sidebar
    // convention deliberately; only surfaces on the admin shell (the
    // driver PWA renders no sidebar). Both slots share it now.
    sidebar: '#F8F8FA',
    sidebarText: '#1A1A1F',
    sidebarMute: '#5A5A62',
    sidebarActive: '#21c163',
    // The accent (#2A2A2F) is invisible on the fixed-dark nav panel; its own admin highlight green reads at about 5.4:1.
    navActive: '#21c163',
    border: '#D8D8DC',
    borderStrong: '#B8B8BE',
    // Dark accent — the contrast story. #2A2A2F gives high contrast both
    // as button bg (vs onAccent #FAFAFA) and as a foreground tint (vs
    // surface #F2F2F4). Replaces the near-white #FAFAFA accent that read
    // as white-on-white.
    accent: '#2A2A2F',
    accentSoft: 'rgba(42, 42, 47, 0.14)',
    // Light text on the now-dark accent button; matches bg.
    onAccent: '#FAFAFA',
    charging: '#21c163',
    chargingGlow: 'rgba(50, 50, 55, 0.50)',
    mute: '#8A8A92',
    text: '#1A1A1F',
    textMute: '#5A5A62',
    // Dark-grey 3-stop card glow on the white surface.
    cardGlow:
      '0 0 0 1px rgba(40, 40, 46, 0.18),'
      + ' 0 0 24px rgba(40, 40, 46, 0.14),'
      + ' 0 8px 32px rgba(40, 40, 46, 0.10)',
    cardGlowSubtle:
      '0 0 0 1px rgba(40, 40, 46, 0.10),'
      + ' 0 0 14px rgba(40, 40, 46, 0.07),'
      + ' 0 4px 12px rgba(40, 40, 46, 0.05)',
    // Widget + chrome glass — light-surface recipes, neutral grey ring.
    widgetBg: 'rgba(255, 255, 255, 0.82)',
    widgetBorder: 'rgba(20, 20, 30, 0.08)',
    widgetShadow:
      '0 8px 24px rgba(20, 20, 30, 0.10),'
      + ' 0 0 0 1px rgba(40, 40, 46, 0.10)',
    chromeBg: 'rgba(248, 248, 250, 0.94)',
    chromeBorder: 'rgba(20, 20, 30, 0.06)',
    chromeShadow: '0 4px 16px rgba(20, 20, 30, 0.10)',
  },
};

/**
 * Light slot — the REACHABLE variant under `availableModes: ['light']`.
 * Holds the dark-accent-on-white design (the higher-contrast tokens that
 * used to live in the dark variant). This is the whole point of commit 6:
 * the light slot intentionally carries the higher-contrast design.
 */
export const electricLightLight: ThemeVariant = {
  mode: 'light',
  tokens: electricLightTokens,
};

/**
 * Dark slot — contract-required but UNREACHABLE (the provider clamps
 * Electric Light to 'light'). Same token body as the light slot.
 */
export const electricLightDark: ThemeVariant = {
  mode: 'dark',
  tokens: electricLightTokens,
};
